import React, {memo, useEffect, useRef, useState} from 'react';
import Collapse, {Panel} from 'rc-collapse';
import moment from 'moment';
import _isEqual from 'lodash/isEqual';
import { useSelector } from "react-redux";

import {HDatePicker, HCheckbox, HInput, HookForm, HCheckboxRadio, DSelect} from '../../../../../validation/HForm';
import calculationLayoutSchema from './calculationLayoutSchema';
import {handleKeyPress, returnEndStr} from '../../../../../helpers/common';
import {QUICK_CALCULATION_DATA} from '../../../../../constants/localStorageConstants';
import DebtResults from "../../../DebtLoadCalculation/components/DebtResults";
import GiveAmountClient from "../components/GiveAmountClient";
import DateInfo from '../components/DateInfo';
import CouponInfo from '../components/CouponInfo';
import PaymentSchedule from '../AnnuityCalculation/PaymentSchedule/PaymentSchedule';
import { optionsPaymentAnnuityDate } from '../../../../../helpers/data-constants';
import { defaultFormState } from '../../constants';
import "./calculation-layout.scss";
import {useDebtLoadCalculation} from "../../../DebtLoadCalculation/useDebtLoadCalculation.hook";

const CalculationLayout = memo(({
  defaultFormValues,
  calculationResult = {},
  onUpdate,
  typeTabValue,
  amountError,
  onClickAvailableAmount,
  minDuration,
  maxDuration,
  calculationFaild,
  setContextCalculationLayout,
  isCalculationFromAmount,
  isEstimatedValue,
  setWaiverOfRedemption,
  watchEstimate,
  estimatedValue,
  isClear,
  shouldRenderWaiverOfRedemption,
  clientId,
  isHybrid = false,
  isAnnuity = false
}) => {
  const [form, setForm] = useState(null);
  const [previousAmount, setPreviousAmount] = useState('0');
  const { accessFeatures } = useSelector(({ user }) => user);
  const { debtLoadCalculation, debtCalculateError, debtLoadCalculationLoading } = useDebtLoadCalculation(clientId, calculationResult);
  let classNameAvailableAmount = 'form-element-data static';
  const firstInputRef = useRef(null);
  const secondInputRef = useRef(null);
  const isNonCashOperationCrediton = accessFeatures['non_cash.operations.type'];
  const isDurationCoupon = accessFeatures["client.category"];
  const isDebtLoadCalculation = calculationResult.calculateDebtLoad && clientId;
  const isDebtCalculateResult = (calculationResult.calculateDebtLoad && debtLoadCalculation?.debtOverloadRate) || debtCalculateError;
  const isRegularCalculation = !isAnnuity && !isHybrid
  const thingsTab = typeTabValue === 1
  const isEstimatedEqualsAmount = accessFeatures['things.calculation.estimated.equals.amount'] && thingsTab

  const minDate =
        minDuration !== null
            ? minDuration === 0
                ? moment().add(minDuration, 'days').format('YYYY-MM-DD')
                : moment()
                    .add(minDuration - 1, 'days')
                    .format('YYYY-MM-DD')
            : moment().add(2, 'days').format('YYYY-MM-DD');
    const maxDate = maxDuration
        ? moment()
            .add(maxDuration - 1, 'days')
            .format('YYYY-MM-DD')
        : moment().add(28, 'days').format('YYYY-MM-DD');

    useEffect(() => {
        if (form) {
            if (!_isEqual({}, defaultFormValues)) {
                form.setValue('calculation', defaultFormValues);
                if(isAnnuity || isHybrid) {
                  form.setValue("calculation.months", {
                    label: defaultFormValues.months?.label,
                    value: defaultFormValues.months?.value,
                  });
                }
            } else {
                form.setValue('calculation', defaultFormState.calculation);
                setWaiverOfRedemption(false)
            }
        }
    }, [form, isClear, defaultFormValues.waiverOfRedemption]);

    useEffect(() => {
        if (form) {
            if (!amountError) {
                form.clearErrors('calculation.amount');
            }

            if (amountError?.startsWith('Сумма')) {
                form.setError('calculation.amount', {
                    type: 'manual',
                    message: amountError,
                });
            }
        }
    }, [amountError, form]);

    const handleChange = () => {
        const formData = form.getValues()
        setWaiverOfRedemption(formData.calculation.waiverOfRedemption)
        setPreviousAmount(formData.calculation.amount)
        form.setValue('calculation.amount', formData.calculation.amount === '' ? '0' : formData.calculation.amount);
        onUpdate(form.getValues());
    };

    const handleChangePaymentTerm = () => {
      if (!form) return;
      onUpdate(form.getValues());
    };

    const onFocusChange = () => {
        const formData = form.getValues()
        form.setValue('calculation.amount', formData.calculation.amount === '0' ? '' : formData.calculation.amount);
        onUpdate(form.getValues());
    }

    const setAmount = (newValue) => {
        form.setValue('calculation.amount', newValue);
        handleChange();
    };

    useEffect(() => {
      if (isEstimatedEqualsAmount && form) {
        const formData = form.getValues();
        form.setValue('calculation.amount',  estimatedValue || formData.calculation.amount);
        if(previousAmount === formData.calculation.amount) {
          onUpdate(form.getValues());
        }
      }
    }, [estimatedValue, previousAmount])

    useEffect(() => {
        if (form) {
            if (previousAmount === '0' && previousAmount !== '' && !calculationFaild) {
                if (JSON.parse(localStorage.getItem(QUICK_CALCULATION_DATA))
                    && JSON.parse(localStorage.getItem(QUICK_CALCULATION_DATA))?.calculation
                    && JSON.parse(localStorage.getItem(QUICK_CALCULATION_DATA)).calculation?.amount) {
                    let x = JSON.parse(localStorage.getItem(QUICK_CALCULATION_DATA))
                    x.calculation.amount = calculationResult.amount.toString()
                    localStorage.setItem('QUICK_CALCULATION_DATA', JSON.stringify(x))
                }
            }
        }
    }, [calculationResult.amount, form, previousAmount, calculationFaild, calculationResult]);

    const clickAvailableAmount = (value) => {
        if (typeTabValue) {
            if (accessFeatures['calculationAmountContractsThings']) {
                onClickAvailableAmount(value);
            } else {
                onClickAvailableAmount(value);
            }
        }
    };

    useEffect(() => {
        if (isClear && form)
            form.clearErrors()
    }, [isClear])

  const renderDateField = () => {
    if (isAnnuity || isHybrid) return null;
    
    return (
      <div className="form-element calculationLayout-element">
        <label className="form-element-label" htmlFor="endDate">
          Дата
        </label>
        <div className="form-element-data date">
          <HDatePicker
            h="true"
            name="calculation.endDate"
            onBlur={handleChange}
            className="input"
            id="endDate"
            min={isDurationCoupon ? moment().format("YYYY-MM-DD") : minDate}
            max={isDurationCoupon ? moment().add(29, "days").format("YYYY-MM-DD") : maxDate}
            onKeyPress={handleKeyPress}
            ref={(e) => {
              form.register(e);
              if (secondInputRef.current === null) {
                secondInputRef.current = e;
              }
            }}
          />
        </div>
      </div>
    );
  };

  const renderMonthsField = () => {
    if (isRegularCalculation) return null;
    
    return (
      <div className="form-element calculationLayout-element-width">
        <label className="form-element-label" htmlFor="months">
          Срок выдачи, мес
        </label>
        <div className="content-datatable-header-search">
          <DSelect
            h="true"
            name="calculation.months"
            placeholder="Выберите срок"
            options={optionsPaymentAnnuityDate}
            noOptionsMessage={() => "Укажите сумму"}
            loadingMessage={() => "Загрузка..."}
            handleOptions={handleChangePaymentTerm}
          />
        </div>
      </div>
    );
  };

  const renderFirstPaymentDate = () => {
    if (!isHybrid) return null;
    
    return (
      <div className="form-element calculationLayout-element">
        <label className="form-element-label" htmlFor="firstPaymentDate">
          Дата первого платежа
        </label>
        <div className="form-element-data date firstPaymentDate">
          <HDatePicker
            h="true"
            name="calculation.firstPaymentDate"
            className="input"
            id="firstPaymentDate"
            min={isDurationCoupon ? moment().format("YYYY-MM-DD") : minDate}
            max={isDurationCoupon ? moment().add(29, "days").format("YYYY-MM-DD") : maxDate}
            onBlur={handleChange}
          />
        </div>
      </div>
    );
  };

  const renderWaiverOfRedemption = () => {
    if (!shouldRenderWaiverOfRedemption || isAnnuity || isHybrid) return null;
    
    return (
      <div className={typeTabValue === 2 ? "calculationLayout-element-hidden" : "form-element calculationLayout-element"}>
        <label className="form-element-label font-size-larger" htmlFor="waiverOfRedemption">
          Отказ от выкупа
        </label>
        <div className="form-element-data clients">
          <HCheckbox
            h
            name="calculation.waiverOfRedemption"
            className="input"
            id="waiverOfRedemption"
            onChange={handleChange}
            disabled={typeTabValue === 2}
          />
        </div>
      </div>
    );
  };

  const renderPaymentInfo = () => {
    if (isAnnuity || isHybrid) {
      return (
          <>
              <div className="form-row">
                  <div className="form-row-item">
                      <div className="form-element calculationLayout-element collection-item">
                          <div className="form-element-data static">
                              <p className="big">Доступно</p>
                          </div>
                      </div>
                  </div>
                  <div className="form-row-item">
                      <div
                          className={typeTabValue !== 0 && accessFeatures["things.calculation.available"] ? classNameAvailableAmount + " pointer" : classNameAvailableAmount}>
                          <p
                              className="big font-bold"
                              onClick={typeTabValue !== 0 && accessFeatures["things.calculation.available"] ? () => clickAvailableAmount(calculationResult.availableAmount) : null}
                          >
                              {calculationResult.availableAmount !== 0 ? calculationResult.availableAmount.toFixed(2) : 0} руб.
                          </p>
                      </div>
                  </div>
              </div>
              <div className="form-row">
                  <div className="form-row-item">
                      <div className="form-element calculationLayout-element collection-item">
                          <div className="form-element-data static">
                              <p className="big">Общая сумма выплат</p>
                          </div>
                      </div>
                  </div>
                  <div className="form-row-item">
                      <div>
                          <p className="big font-bold">
                              {calculationResult.refundAmount !== 0 ? calculationResult.refundAmount.toFixed(2) : 0} руб.
                          </p>
                      </div>
                  </div>
              </div>
              <div className="form-row">
                  <div className="form-row-item">
                      <div className="form-element calculationLayout-element collection-item">
                          <div className="form-element-data static">
                              <p className="big">Переплата</p>
                          </div>
                      </div>
                  </div>
                  <div className="form-row-item">
                      <div>
                          <p className="big font-bold">
                              {calculationResult.overpaymentTotal !== 0 ? calculationResult.overpaymentTotal.toFixed(2) : 0} руб.
                          </p>
                      </div>
                  </div>
              </div>
              {calculationResult.payments?.length > 1 && (
                  <div className="form-row">
                      <div className="form-row-item">
                          <div className="form-element calculationLayout-element collection-item">
                              <div className="form-element-data static">
                                  <p className="big">Сумма ежемесячного аннуитетного платежа</p>
                              </div>
                          </div>
                      </div>
                      <div className="form-row-item">
                          <div>
                              <p className="big font-bold">
                                  {calculationResult.payments[1].paymentAmount.toFixed(2)} руб.
                              </p>
                          </div>
                      </div>
                  </div>
              )}
              {isHybrid && (
                  <div className="calculation-available">
                      <div className="info-container">
                          <p className="info-block">
                              <strong>Первый период</strong> идет погашение только процентов за пользование займом.
                          </p>
                          <p className="info-block">
                              В дальнейшем применяется аннуитетная схема погашения.
                          </p>
                      </div>
                  </div>
              )}
          </>
      );
    }

      return (
          <>
              <div className="form-row">
                  <div className="form-row-item">
                      <div className="form-element calculationLayout-element collection-item">
                          <div className="form-element-data static">
                              <p className="big">Доступно</p>
                          </div>
                      </div>
                  </div>
                  <div className="form-row-item">
                      <div
                          className={typeTabValue !== 0 && accessFeatures["things.calculation.available"] ? classNameAvailableAmount + " pointer" : classNameAvailableAmount}>
                          <p
                              className="big font-bold"
                              onClick={typeTabValue !== 0 && accessFeatures["things.calculation.available"] ? () => clickAvailableAmount(calculationResult.availableAmount) : null}
                          >
                              {calculationResult.availableAmount !== 0 ? calculationResult.availableAmount.toFixed(2) : 0} руб.
                          </p>
                      </div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-row-item">
            <div className="form-element calculationLayout-element collection-item">
              <div className="form-element-data static">
                <p className="big">Максимальная сумма до {calculationResult.date}</p>
              </div>
            </div>
          </div>
          <div className="form-row-item">
            <div className={accessFeatures["calculationAmountContractsThings"] && thingsTab ? classNameAvailableAmount : classNameAvailableAmount + " pointer"}>
              <p
                className="big font-bold"
                onClick={() => accessFeatures["calculationAmountContractsThings"] && typeTabValue !== 1 && setAmount(calculationResult.maxAmountToDate)}
              >
                {calculationResult.maxAmountToDate !== 0 ? calculationResult.maxAmountToDate.toFixed(2) : 0} руб.
              </p>
            </div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-row-item">
            <div className="form-element calculationLayout-element calculationLayout-width">
              <div className="form-element-data static">
                <Collapse>
                  <Panel
                    showArrow={false}
                    forceRender={true}
                    header={`Сумма к возврату через ${returnEndStr(calculationResult.days)}`}
                    headerClass="block-datacollapse-header calculationLayout-header"
                    className="block-datacollapse-container calculationLayout-container collection-item"
                  >
                    <table className="calculationLayout-table">
                      <tbody>
                        <tr>
                          <td><p>1</p></td>
                          <td><p>За пользование микрозаймом</p></td>
                          <td>
                            <p>
                              {calculationResult.overpaymentTotal !== 0 ? calculationResult.overpaymentTotal.toFixed(2) : 0} руб.
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td><p>2</p></td>
                          <td><p>НДС</p></td>
                          <td>
                            <p>
                              {calculationResult.refundableAmount?.nds || "0.00"} руб.
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td><p>3</p></td>
                          <td><p>Микрозаём</p></td>
                          <td>
                            <p>
                              {calculationResult.amount !== 0 ? calculationResult.amount.toFixed(2) : 0} руб.
                            </p>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </Panel>
                </Collapse>
              </div>
            </div>
          </div>
          <div className="form-row-item">
            <div className={classNameAvailableAmount}>
              <p className="big font-bold">
                {calculationResult.refundAmount !== 0 ? calculationResult.refundAmount.toFixed(2) : 0} руб.
              </p>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <HookForm
        validationSchema={calculationLayoutSchema(minDate, maxDate, isAnnuity, isHybrid)}
        defaultValues={defaultFormState}
        predef={(methods) => {
          if (!form) {
            setForm(methods);
            setContextCalculationLayout(methods);
          }
        }}
      >
        <fieldset className='d-flex flex-direction-row columns-2'>
          <div className='calculation'>
            <div className="form-row">
              <div className="form-row-item">
                <div className="form-element calculationLayout-element-width ">
                  <label className="form-element-label" htmlFor="amount">
                    Сумма, руб.
                  </label>
                  <div className="form-element-data number">
                  <HInput
                      h="true"
                      type="text"
                      name="calculation.amount"
                      onBlur={() => {
                        handleChange();
                        isCalculationFromAmount(true);
                        isEstimatedValue(false);
                      }}
                      className={`input ${isDebtLoadCalculation ? 'invalid' : ''}`}
                      id="amount"
                      decimals={2}
                      onFocus={onFocusChange}
                      onKeyPress={handleKeyPress}
                      onChange={() => {
                        watchEstimate("");
                      }}
                      ref={(e) => {
                        form.register(e);
                        if (firstInputRef.current === null) {
                          firstInputRef.current = e;
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="form-row-item">
                {renderDateField()}
                {renderFirstPaymentDate()}
                {renderMonthsField()}
              </div>
              <DateInfo
                redemptionDate={calculationResult.date}
                countDays={calculationResult.days}
                firstPaymentDays={calculationResult.firstPaymentDays}
                paymentDate={calculationResult.payments?.length && calculationResult.payments[0].paymentDate}
              />
              <div className={"form-row-item d-flex flex-row columns-2 clients"}>
                {renderWaiverOfRedemption()}
                {isNonCashOperationCrediton && (
                  <div className="form-element calculationLayout-element">
                    <label className="form-element-label font-size-larger" htmlFor="isNonCash">
                      Форма оплаты
                    </label>
                    <HCheckboxRadio
                      h
                      type="checkbox"
                      name="calculation.isNonCash"
                      id="isNonCash"
                      onChange={handleChange}
                      title={"БЕЗНАЛ"}
                    />
                  </div>
                )}
              </div>
            </div>
            <CouponInfo
              calculationResult={calculationResult}
              accessFeatures={accessFeatures}
            />
            <div className="collection d-flex">
              <div>
                {renderPaymentInfo()}
                <GiveAmountClient amount={calculationResult.amount} />
              </div>
              <div className='debt-results'>
                {isDebtCalculateResult && (
                  <DebtResults
                    debtLoadCalculation={debtLoadCalculation}
                    calculateError={debtCalculateError}
                    debtLoadCalculationLoading={debtLoadCalculationLoading}
                  />
                )}
              </div>
            </div>
            {(isAnnuity || isHybrid) && (
              <PaymentSchedule
                calculationResult={calculationResult}
                amountError={amountError}
                isHybrid={isHybrid}
              />
            )}
          </div>
        </fieldset>
      </HookForm>
    </>
  );
});

export default CalculationLayout;
